import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography, } from "@mui/material";
import { EditIcon } from "src/icons/EditIcon";
import { DeleteIcon } from "src/icons/DeleteIcon";
import React, { useEffect, useState } from "react";
import { ManufacturerRecommendationForUser } from "src/model/manufacturer-recommendation-project";
import {
  useCreateUserManufacturerMutation,
  useDeleteUserManufacturerMutation,
  useGetAllManufacturerDiscountForProjectQuery,
  useGetManufacturerRecommendationForProjectQuery,
  useUpdateUserManufacturerMutation
} from "src/api/v1/manufacturer-discount-ApiSlice";
import { useParams } from "react-router";
import { useGetAuthKeyQuery } from "src/api/auth-token-ApiSlice";
import { ManufacturerRecommendationResponse } from "src/model/api/manufacturer-recommendation";
import { GetAllManufacturerDiscountForProjectResponse } from "src/model/api/manufacturer-discount.model";
import { DeleteManufacturerDialog } from "src/components/ManufacturerDialogs/DeleteManufacturerDialog";
import { RenameManufacturerDialog } from "src/components/ManufacturerDialogs/RenameManufacturerDialog";

interface ManufacturerAutocompleteProps {
    setSelectedManufacturer: (manufacturer: ManufacturerRecommendationForUser | null) => void;
    selectedManufacturer: ManufacturerRecommendationForUser | null;
    preSelectedManufacturerId?: number | null;
    preSelectedManufacturerType?: string | null;
}

type CustomManufacturerOption =
    | ManufacturerRecommendationForUser
    | { inputValue: string; custom: true };

export const ManufacturerAutocomplete = ({ setSelectedManufacturer, selectedManufacturer, preSelectedManufacturerId, preSelectedManufacturerType }: ManufacturerAutocompleteProps) => {
    const [manufacturerList, setManufacturerList] = useState<
        ManufacturerRecommendationForUser[]
    >([]);
    const [deletedManufacturer, setDeletedManufacturer] = useState<any>(null);

    const [deleteManufacturerOpen, setDeleteManufacturerOpen] = useState(false);

    const [editManufacturerOpen, setEditManufacturerOpen] = useState(false);
    const [editedName, setEditedName] = useState("");

    const [createUserManufacturer] = useCreateUserManufacturerMutation();
    const [updateUserManufacturer] = useUpdateUserManufacturerMutation();
    const [deleteUserManufacturer] = useDeleteUserManufacturerMutation();

    const token = useGetAuthKeyQuery();
    const params = useParams<{ projectId: string }>();

    const { data: allManufacturerDiscountForProject } =
        useGetAllManufacturerDiscountForProjectQuery(
            Number(params.projectId),
            {
                skip: !token.data?.token,
            }
        );

    const { data: manufacturerRecommendationForProject } =
        useGetManufacturerRecommendationForProjectQuery(
            Number(params.projectId),
            {
                skip: !token.data?.token,
            }
        );

    function getAllManufacturersList(
        manufacturerRecommendationForProject: ManufacturerRecommendationResponse,
        allManufacturerDiscountForProject: GetAllManufacturerDiscountForProjectResponse
    ): ManufacturerRecommendationForUser[] {
        return [
            ...Object.values(manufacturerRecommendationForProject),
            ...Object.values(allManufacturerDiscountForProject.internal_manufacturer_discount ?? {}),
            ...Object.values(allManufacturerDiscountForProject.user_manufacturer_discount ?? {}),
        ];
    }

    useEffect(() => {
        if (!manufacturerRecommendationForProject || !allManufacturerDiscountForProject) {
            return;
        }

        const allManufacturers = getAllManufacturersList(
            manufacturerRecommendationForProject,
            allManufacturerDiscountForProject
        );

        setManufacturerList(allManufacturers);
    }, [manufacturerRecommendationForProject, allManufacturerDiscountForProject]);

    useEffect(() => {
        if (deletedManufacturer) {
            setDeletedManufacturer(null);
        }
    }, [deletedManufacturer]);

    useEffect(() => {
        if (!preSelectedManufacturerId || manufacturerList.length === 0) return;

        const manu = manufacturerList.find((m) => {
            if (preSelectedManufacturerType === "internal") {
                return m.internal_product_manufacturer_id === Number(preSelectedManufacturerId);
            } else {
                return m.user_manufacturer_id === Number(preSelectedManufacturerId);
            }
        }) || null;

        setSelectedManufacturer(manu);
    }, [manufacturerList, preSelectedManufacturerId, preSelectedManufacturerType, setSelectedManufacturer]);

    const handleEditManufacturerOpen = (manufacturer: ManufacturerRecommendationForUser) => {
        setSelectedManufacturer(manufacturer);
        setEditedName(manufacturer.manufacturer_name || "");
        setEditManufacturerOpen(true);
    };

    const handleEditManufacturerClose = () => {
        setSelectedManufacturer(null);
        setEditedName("");
        setEditManufacturerOpen(false);
    };

    const handleDeleteManufacturerOpen = (
        manufacturer: ManufacturerRecommendationForUser
    ) => {
        setSelectedManufacturer(manufacturer);
        setDeleteManufacturerOpen(true);
    };

    const handleDeleteManufacturerClose = () => {
        setSelectedManufacturer(null);
        setDeleteManufacturerOpen(false);
    };

    const handleDeleteConfirm = () => {
        if (!selectedManufacturer?.user_manufacturer_id) return;

        deleteUserManufacturer({
            user_manufacturer_id: selectedManufacturer.user_manufacturer_id,
        }).catch((err) => {
            console.error("Delete error:", err);
        });

        handleDeleteManufacturerClose();
    };

    const handleEditConfirm = () => {
        if (!selectedManufacturer?.user_manufacturer_id) return;

        updateUserManufacturer({
            user_manufacturer_id: selectedManufacturer.user_manufacturer_id,
            manufacturer_name: editedName,
        }).catch((err) => {
            console.error("Edit error:", err);
        });

        handleEditManufacturerClose();
    };

    return (
        <>
            <Autocomplete<CustomManufacturerOption>
                size="small"
                options={manufacturerList}
                value={selectedManufacturer}
                getOptionLabel={(option) =>
                    "manufacturer_name" in option
                        ? option.manufacturer_name
                        : option.inputValue
                }
                filterOptions={(options, state) => {
                    const inputValueLower: string = state.inputValue.toLowerCase();
                    let isExisting = false;

                    const filtered: CustomManufacturerOption[] = options.filter((o) => {
                        if ("manufacturer_name" in o) {
                            const nameLower = o.manufacturer_name.toLowerCase();
                            if (nameLower === inputValueLower) {
                                isExisting = true;
                            }
                            return nameLower.includes(inputValueLower);
                        }
                        return false;
                    });

                    if (state.inputValue !== "" && !isExisting) {
                        filtered.push({ inputValue: state.inputValue, custom: true });
                    }

                    return filtered;
                }}
                renderOption={(props, option) => {
                    if ("custom" in option) {
                        const { key, onClick, ...rest } = props;
                        return (
                            <Box
                                key={key}
                                component="li"
                                {...rest}
                                sx={{
                                    width: "100%",
                                    padding: 0,
                                    "&:hover": { backgroundColor: "transparent" },
                                    "&.MuiAutocomplete-option:hover": {
                                        backgroundColor: "transparent",
                                    },
                                }}
                            >
                                <Button
                                    fullWidth
                                    onClick={(event) => {
                                        if (onClick) {
                                            onClick(
                                                event as unknown as React.MouseEvent<HTMLLIElement>
                                            );
                                        }
                                    }}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        backgroundColor: "hsl(218, 89%, 63%)",
                                        borderRadius: "6px",
                                        padding: "8px 12px",
                                        color: "white",
                                        textTransform: "none",
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        "&:hover": {
                                            backgroundColor: "hsl(218, 89%, 56%)",
                                        },
                                    }}
                                >
                                    <Typography variant="body2" component="span">
                                        Add{" "}
                                        <strong style={{ fontWeight: 600 }}>
                                            {option.inputValue}
                                        </strong>{" "}
                                        as new manufacturer
                                    </Typography>
                                </Button>
                            </Box>
                        );
                    }

                    const { key, ...rest } = props;

                    return (
                        <Stack
                            key={key}
                            component="li"
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            borderRadius="6px"
                            fontSize="12px"
                            px={1}
                            sx={{
                                "&[aria-selected='true']&.MuiAutocomplete-option:hover": {
                                  backgroundColor: "hsl(218, 89%, 63%)",
                                },
                                "&[aria-selected='true']": {
                                    backgroundColor: "hsl(218, 89%, 63%)",
                                },
                                "&.MuiAutocomplete-option:hover": {
                                    backgroundColor: "hsl(218, 89%, 63%)",
                                },
                            }}
                            {...rest}
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {option.manufacturer_name}
                            </Box>

                            {option.user_manufacturer_id && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditManufacturerOpen(option);
                                        }}
                                        sx={{
                                            color: "inherit",
                                            "&:hover": { color: "white" },
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteManufacturerOpen(option);
                                        }}
                                        sx={{
                                            color: "inherit",
                                            "&:hover": { color: "white" },
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                    );
                }}
                renderInput={(params) => <TextField {...params} />}
                onChange={async (event, value) => {
                    if (value && "custom" in value) {
                        const manufacturerName = value.inputValue;
                        createUserManufacturer({ manufacturer_name: manufacturerName })
                            .then(() => {
                                const newManu = {
                                    internal_product_manufacturer_id: -1,
                                    manufacturer_name: manufacturerName,
                                };
                                setManufacturerList((prev) => [...prev, newManu]);
                                setSelectedManufacturer(newManu);
                            })
                            .catch((error) => console.error("Error:", error));
                    } else if (value == null) {
                        setSelectedManufacturer(null);
                        setDeletedManufacturer({
                            manufacturer_name: "",
                            internal_product_manufacturer_id: "",
                            user_manufacturer_id: "",
                            fetchedManufacturer: false,
                        });
                    } else if ("internal_product_manufacturer_id" in value) {
                        const v = value as ManufacturerRecommendationForUser;
                        setSelectedManufacturer({
                            ...v,
                            user_manufacturer_id: undefined,
                        });
                    } else if ("user_manufacturer_id" in value) {
                        const v = value as ManufacturerRecommendationForUser;
                        setSelectedManufacturer({
                            ...v,
                            internal_product_manufacturer_id: undefined,
                        });
                    }
                }}
            />

            <DeleteManufacturerDialog
                open={deleteManufacturerOpen}
                manufacturerName={selectedManufacturer?.manufacturer_name ?? ''}
                onClose={handleDeleteManufacturerClose}
                onConfirm={handleDeleteConfirm}
            />

            <RenameManufacturerDialog
                open={editManufacturerOpen}
                manufacturerName={selectedManufacturer?.manufacturer_name ?? ''}
                editedName={editedName}
                onChange={setEditedName}
                onClose={handleEditManufacturerClose}
                onConfirm={handleEditConfirm}
            />
        </>
    );
};
